import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, Save, Trash2, Info } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatCurrency } from '@/lib/format';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useScopeStore, type NodesResponse } from '@/stores/scope';
import { useScopePinStore } from '@/stores/scope-pin';
import { currentFiscalYear, fiscalPositionOfMonth } from '@/lib/fiscal-year';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/masters/kpi-targets` (v0.4 追加要件⑭ 増分③、D-466①)。
 * 年間 / 四半期 / 月別直接 の3つの入力方式で売上目標を登録する。
 *
 * ⚠️ 3方式は独立したモードではなく「どの段から入力を始めるか」の違いにすぎない (設計 §4-1)。
 *    タブ切替は plan 行の作成/削除に対応し、テナント設定としての「モード」列は持たない。
 * ⚠️ D-461: `by_days` の日数基準 (営業日数 / 暦日数) を必ず画面に出すこと。
 *    無言で暦日数に落ちると「営業日数比を選んだのに暦日数だった」= §8-122 の逆パターンになる。
 *
 * 設計: docs/design/v04-sales-target-planning-detail.md §7-1
 */

// ⚠️ ui-guidelines §2.2 / §7.2: focus のリングに brand-red を使わない (赤は CTA 専用)。
const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus:outline-none focus:shadow-focus-ring';
// ⚠️ ネイティブスピナーは抑制する (D-211 で指摘済みのパターン)
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

type TargetRow = {
  kpi_target_id: string; node_id: string; kpi_type: string;
  target_month: string; target_value: number; source: 'manual' | 'plan_allocated'; plan_id: string | null;
};
type PlanRow = {
  plan_id: string; node_id: string; kpi_type: string; period_kind: 'year' | 'quarter';
  fiscal_year: number; quarter: number | null; target_value: number;
  allocation_method: AllocationMethod; allocation_weights: Record<string, number>;
};
type TargetsResponse = {
  fiscal_year: number; fiscal_year_start_month: number; fiscal_year_label: string;
  months: string[]; targets: TargetRow[]; plans: PlanRow[];
};
type PreviewResponse = {
  months: string[]; allocation_basis: 'business_days' | 'calendar_days';
  days_by_month: Record<string, number>; allocation: { month: string; value: number }[]; total: number;
};

type AllocationMethod = 'equal' | 'by_days' | 'manual_weights';
type InputMode = 'year' | 'quarter' | 'monthly';

const ALLOCATION_LABELS: Record<AllocationMethod, string> = {
  equal: '等分',
  by_days: '日数比',
  manual_weights: '比率を指定',
};
const BASIS_LABELS: Record<PreviewResponse['allocation_basis'], string> = {
  business_days: '営業日数',
  calendar_days: '暦日数',
};

const monthLabel = (month: string): string => {
  const [, m] = month.split('-').map((s) => Number.parseInt(s, 10));
  return `${m}月`;
};

const monthLabelFull = (month: string): string => {
  const [y, m] = month.split('-').map((s) => Number.parseInt(s, 10));
  return `${y}年${m}月`;
};

// ---------------------------------------------------------------------------
// 税込額 → 税抜額の換算 (Full 限定、docs/design/v05-consumption-tax-rate-and-target-tax-basis.md §6-1/§6-8)
// 保存 API は常に税抜を受け取るため、換算は保存前に画面で明示的に行う。
// 換算は常に「税込欄」の値から計算し直す (再押下しても同じ結果になり、二重に割られない)。
// ---------------------------------------------------------------------------
type ConversionErrorCode = 'rate_not_set' | 'rate_changes_within_period';
type ConversionItem = {
  converted_value: number | null; standard_rate: number | null;
  error_code: ConversionErrorCode | null; error: string | null;
};
type ConversionFailure = { message: string; code: ConversionErrorCode | null };
type ConversionResponse = { items: ConversionItem[] };

const TAX_NOTE = '目標は標準税率のみで換算します（軽減税率分は区別しません）';

const parseTaxIncluded = (text: string): number | null => {
  if (text.trim() === '') return null;
  const n = Number(text);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

const formatRatePercent = (rate: number) => `${Math.round(rate * 10000) / 100}%`;

/** 税率未設定なら消費税率タブへ、期間中の税率改定なら月別入力へ案内する。 */
function TaxConversionError({ failure }: { failure: ConversionFailure }) {
  return (
    <p role="alert" className="text-xs text-danger-text bg-danger-bg border border-danger-border rounded px-2 py-1 mt-1">
      {failure.message}
      {failure.code === 'rate_not_set' && (
        <>{' '}<Link to="/settings/consumption-tax-rates" className="underline">消費税率の設定</Link></>
      )}
      {failure.code === 'rate_changes_within_period' && '（下の「月別の目標」で「税込で入力」を使ってください）'}
    </p>
  );
}

export function KpiTargetsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = hasPermission(user?.effective_permissions, 'kpi_target', 'manage');
  // 消費税率マスタは Full 限定のため、税込からの換算 UI も Full のみ出す
  const isFull = user?.edition !== 'lite';

  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-4): NodeSwitcherの選択を初期値として
  // 片方向シードする (既存不整合の解消)。ただし対象組織セレクタの変更・ドリルダウン等の
  // ページ内操作はローカルnodeIdのみを書き換え、useScopeStore側へは一切書き戻さない。
  // 「全社」(selectedNodeId===null) の場合はシードせず、既存の先頭ノードへのフォールバックを温存する
  // (GET /kpi-targets/progress がnode_id必須のため「全社」目標という概念自体がバックエンドに無い)。
  const globalSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const [nodeId, setNodeId] = useState('');
  useEffect(() => {
    if (!nodeId && globalSelectedNodeId) setNodeId(globalSelectedNodeId);
  }, [globalSelectedNodeId]);
  const effectiveNodeId = nodeId || flatNodes[0]?.node.node_id || '';

  const [fiscalYear, setFiscalYear] = useState<number | null>(null);
  const isPinned = useScopePinStore((s) => s.isPinned);
  const pinnedPeriodEnd = useScopePinStore((s) => s.pinnedPeriodEnd);

  // ⚠️ 年度の境界は `tenant_settings.fiscal_year_start_month` に依存するため、
  //    それを知る前に fiscal_year を決め打ちすると10月始まりのテナントで1年ズレる
  //    (しかも queryKey が変わらないので再取得されず、セレクタ表示と実データが食い違ったまま残る)。
  //    ⇒ 先に設定だけ取得し、確定してから年度クエリを有効化する。
  const { data: settings } = useQuery<{ fiscal_year_start_month?: number }>({
    queryKey: ['tenant-settings'],
    queryFn: () => apiFetch<{ fiscal_year_start_month?: number }>('/tenant-settings'),
  });
  const startMonth = settings?.fiscal_year_start_month ?? 4;
  const startMonthReady = settings !== undefined;
  // 5-1: 固定中は固定範囲の終了月が属する年度を既定にする (userが年度セレクタで明示的に
  // 変更した場合はfiscalYearが優先される、これはuseStateではなく毎レンダー導出のためstale値問題は無い)
  const defaultFiscalYear = isPinned && pinnedPeriodEnd
    ? fiscalPositionOfMonth(pinnedPeriodEnd, startMonth).fiscalYear
    : currentFiscalYear(startMonth);
  const resolvedFiscalYear = fiscalYear ?? defaultFiscalYear;

  const { data, isLoading, isError } = useQuery<TargetsResponse>({
    queryKey: ['kpi-targets', effectiveNodeId, resolvedFiscalYear],
    queryFn: () => apiFetch<TargetsResponse>(
      `/kpi-targets?fiscal_year=${resolvedFiscalYear}&node_id=${encodeURIComponent(effectiveNodeId)}`),
    enabled: !!effectiveNodeId && startMonthReady,
  });
  const yearOptions = useMemo(() => {
    const base = defaultFiscalYear;
    return [base - 1, base, base + 1];
  }, [defaultFiscalYear]);

  const [inputMode, setInputMode] = useState<InputMode | null>(null);
  const [quarter, setQuarter] = useState(1);
  const plans = data?.plans ?? [];
  const effectiveMode: InputMode = inputMode ?? (plans[0] ? plans[0].period_kind : 'monthly');
  // ⚠️ 四半期 plan は同一年度に最大4件ありうる。`plans[0]` を編集対象に固定すると、
  //    四半期セレクタを変えたまま保存したときに **別の四半期の plan が書き換わる**
  //    (Q1 の plan が Q3 に化け、Q1 の月次目標が孤児化する)。必ず quarter まで見て選ぶ。
  const plan = plans.find((p) => p.period_kind === effectiveMode
    && (effectiveMode === 'year' || p.quarter === quarter)) ?? null;

  const targetByMonth = useMemo(
    () => new Map((data?.targets ?? []).map((t) => [t.target_month, t])),
    [data?.targets],
  );
  // ⚠️ 期間目標と突き合わせる月次合計は **plan の対象月だけ**に絞ること。
  //    四半期 plan (3ヶ月) と年度12ヶ月の合計を並べると常に巨大な差が出て「差を見せる」意図が壊れる。
  const planMonthSet = useMemo(() => {
    if (!plan || !data) return null;
    return new Set(plan.period_kind === 'year'
      ? data.months
      : data.months.slice(((plan.quarter ?? 1) - 1) * 3, ((plan.quarter ?? 1) - 1) * 3 + 3));
  }, [plan, data]);
  const monthlyTotal = (data?.targets ?? [])
    .filter((t) => !planMonthSet || planMonthSet.has(t.target_month))
    .reduce((a, t) => a + t.target_value, 0);

  if (!effectiveNodeId) {
    return <div className="p-4"><EmptyState message="組織が見つかりません" description="組織階層が設定されていません。" /></div>;
  }

  return (
    <div className="p-4 space-y-4">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-text-main">売上目標</h1>
        <p className="text-sm text-text-sub">年度・四半期・月次の売上目標を登録します。達成状況は「目標と実績」画面で確認できます。</p>
      </header>

      <div className="flex flex-wrap gap-3">
        <div className="min-w-0 w-full sm:w-72">
          <label className="block text-xs text-text-sub mb-1" htmlFor="kt-node">対象組織</label>
          <Select value={effectiveNodeId} onValueChange={setNodeId}>
            <SelectTrigger id="kt-node" className="w-full min-h-[44px]"><SelectValue placeholder="組織を選ぶ" /></SelectTrigger>
            <SelectContent>
              {flatNodes.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 w-full sm:w-72">
          <label className="block text-xs text-text-sub mb-1" htmlFor="kt-fy">年度</label>
          <Select value={String(resolvedFiscalYear)} onValueChange={(v) => setFiscalYear(Number(v))}>
            <SelectTrigger id="kt-fy" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}年度</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isPinned && pinnedPeriodEnd && (
        <p className="text-xs text-text-sub flex items-center gap-1">
          <Info size={12} className="shrink-0" aria-hidden="true" />
          固定範囲（〜{monthLabelFull(pinnedPeriodEnd)}）に基づき{resolvedFiscalYear}年度を表示中
        </p>
      )}

      {data && (
        <p className="text-xs text-text-sub">{data.fiscal_year_label}</p>
      )}

      {isLoading && <div className="flex items-center gap-2 text-text-sub"><Loader2 className="animate-spin" size={16} />読み込み中…</div>}
      {isError && <p className="text-sm text-danger-text">売上目標の取得に失敗しました。時間をおいて再度お試しください。</p>}

      {data && (
        <>
          <ModeTabs mode={effectiveMode} onChange={setInputMode} disabled={!canManage} />
          {effectiveMode !== 'monthly' && (
            // ⚠️ key を付けること。付けないとノード・年度・四半期を切り替えても
            //    入力途中の state (金額・按分方式・比率) が持ち越され、別の組織へ保存されうる (§8-95)。
            <PlanEditor
              key={`${effectiveNodeId}:${resolvedFiscalYear}:${effectiveMode}:${quarter}`}
              nodeId={effectiveNodeId}
              fiscalYear={resolvedFiscalYear}
              periodKind={effectiveMode}
              quarter={quarter}
              onQuarterChange={setQuarter}
              months={planMonthSet ? data.months.filter((m) => planMonthSet.has(m)) : data.months}
              plan={plan}
              canManage={canManage}
              canConvertTax={isFull}
              onSaved={() => qc.invalidateQueries({ queryKey: ['kpi-targets'] })}
            />
          )}
          <MonthlyTable
            key={`${effectiveNodeId}:${resolvedFiscalYear}`}
            months={data.months}
            targetByMonth={targetByMonth}
            nodeId={effectiveNodeId}
            canManage={canManage}
            canConvertTax={isFull}
            onSaved={() => qc.invalidateQueries({ queryKey: ['kpi-targets'] })}
          />
          <FooterSummary planValue={plan?.target_value ?? null} monthlyTotal={monthlyTotal} />
        </>
      )}
    </div>
  );
}

function ModeTabs({ mode, onChange, disabled }: { mode: InputMode; onChange: (m: InputMode) => void; disabled: boolean }) {
  const tabs: { key: InputMode; label: string }[] = [
    { key: 'year', label: '年間目標を按分' },
    { key: 'quarter', label: '四半期目標を按分' },
    { key: 'monthly', label: '月別に直接入力' },
  ];
  return (
    <div role="tablist" aria-label="入力方式" className="flex flex-wrap gap-2 border-b border-surface-border pb-2">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={mode === t.key}
          disabled={disabled}
          onClick={() => onChange(t.key)}
          className={`min-h-[44px] px-4 rounded text-sm ${mode === t.key
            ? 'bg-chrome-base text-white'
            : 'bg-surface-sunken text-text-body hover:bg-surface-line'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function PlanEditor({ nodeId, fiscalYear, periodKind, quarter, onQuarterChange, months, plan, canManage, canConvertTax, onSaved }: {
  nodeId: string; fiscalYear: number; periodKind: 'year' | 'quarter';
  /** ⚠️ 四半期は親が持つ。子の useState にすると「表示中の四半期」と「編集対象の plan」が
   *  ズレて、別の四半期の plan を書き換えてしまう。 */
  quarter: number; onQuarterChange: (q: number) => void;
  /** この plan の対象月 (比率入力の行を出すのに使う) */
  months: string[];
  plan: PlanRow | null; canManage: boolean; canConvertTax: boolean; onSaved: () => void;
}) {
  const [amount, setAmount] = useState<string>(plan ? String(plan.target_value) : '');
  const [method, setMethod] = useState<AllocationMethod>(plan?.allocation_method ?? 'equal');
  const [weights, setWeights] = useState<Record<string, number>>(plan?.allocation_weights ?? {});
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const body = () => ({
    node_id: nodeId, period_kind: periodKind, fiscal_year: fiscalYear,
    quarter: periodKind === 'quarter' ? quarter : null,
    target_value: Number(amount || 0),
    allocation_method: method,
    allocation_weights: method === 'manual_weights' ? weights : null,
  });

  const previewMutation = useMutation({
    mutationFn: () => apiFetch<PreviewResponse>('/kpi-targets/plans/preview', { method: 'POST', body: JSON.stringify(body()) }),
    onSuccess: (r) => setPreview(r),
    onError: (e: Error) => toast.error(e.message),
  });

  const saveMutation = useMutation({
    mutationFn: () => (plan
      ? apiFetch(`/kpi-targets/plans/${plan.plan_id}`, { method: 'PUT', body: JSON.stringify(body()) })
      : apiFetch('/kpi-targets/plans', { method: 'POST', body: JSON.stringify(body()) })),
    onSuccess: () => { toast.success('目標を保存しました'); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch(`/kpi-targets/plans/${plan!.plan_id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('目標を削除しました'); setConfirmDelete(false); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="rounded border border-surface-border bg-surface-card p-4 space-y-3">
      <div className="flex flex-wrap gap-3">
        {periodKind === 'quarter' && (
          <div className="w-full sm:w-40">
            <label className="block text-xs text-text-sub mb-1" htmlFor="kt-q">四半期</label>
            <Select value={String(quarter)} onValueChange={(v) => onQuarterChange(Number(v))}>
              <SelectTrigger id="kt-q" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>第{q}四半期</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-full sm:w-60">
          <label className="block text-xs text-text-sub mb-1" htmlFor="kt-amount">目標額（税抜、円）</label>
          <input
            id="kt-amount" type="number" inputMode="numeric" className={numberInputCls}
            value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!canManage}
          />
        </div>
        <div className="w-full sm:w-48">
          <label className="block text-xs text-text-sub mb-1" htmlFor="kt-method">按分方式</label>
          <Select value={method} onValueChange={(v) => setMethod(v as AllocationMethod)}>
            <SelectTrigger id="kt-method" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(ALLOCATION_LABELS) as AllocationMethod[]).map((m) => (
                <SelectItem key={m} value={m}>{ALLOCATION_LABELS[m]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {canConvertTax && canManage && (
        <PlanTaxConversion nodeId={nodeId} months={months} currentAmount={amount} onConverted={(v) => setAmount(String(v))} />
      )}

      {/* ⚠️ プレビュー後にしか出さないと、比率を入力する手段が無いまま
          「空の比率でプレビュー (=400)」を強いられ、D-451 の3方式のうち1つが到達不能になる。
          対象月は親から渡された months をそのまま使う。 */}
      {method === 'manual_weights' && (
        <WeightsEditor months={preview?.months ?? months} weights={weights} onChange={setWeights} disabled={!canManage} />
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => previewMutation.mutate()} loading={previewMutation.isPending}>
          按分をプレビュー
        </Button>
        <Button variant="primary" leftIcon={<Save size={16} />} onClick={() => saveMutation.mutate()}
          loading={saveMutation.isPending} disabled={!canManage || !amount}>
          保存して按分
        </Button>
        {plan && (
          <Button variant="secondary" leftIcon={<Trash2 size={16} />} onClick={() => setConfirmDelete(true)} disabled={!canManage}>
            この目標を削除
          </Button>
        )}
      </div>

      {preview && (
        <div className="space-y-2">
          {/* ⚠️ D-461: 採用した日数基準を必ず表示する */}
          {method === 'by_days' && (
            <p className="flex items-start gap-2 text-xs text-alert-text bg-alert-bg border border-alert-border rounded px-3 py-2">
              <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                日数の基準: <strong>{BASIS_LABELS[preview.allocation_basis]}</strong>
                {preview.allocation_basis === 'calendar_days' && '（この組織には営業時間が登録されていないため、暦日数で按分します）'}
              </span>
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="relative min-w-full text-sm">
              <caption className="sr-only">按分プレビュー</caption>
              <thead>
                <tr className="text-left text-text-sub border-b border-surface-border">
                  <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap">月</th>
                  {method === 'by_days' && <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap">日数</th>}
                  <th scope="col" className="relative py-2 font-medium whitespace-nowrap text-right">目標額</th>
                </tr>
              </thead>
              <tbody>
                {preview.allocation.map((a) => (
                  <tr key={a.month} className="border-b border-surface-line">
                    <td className="py-2 pr-4 whitespace-nowrap">{monthLabel(a.month)}</td>
                    {method === 'by_days' && <td className="py-2 pr-4 whitespace-nowrap">{preview.days_by_month[a.month] ?? '−'}</td>}
                    <td className="py-2 text-right whitespace-nowrap tabular-nums">{formatCurrency(a.value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-medium">
                  <td className="py-2 pr-4" colSpan={method === 'by_days' ? 2 : 1}>合計</td>
                  <td className="py-2 text-right tabular-nums">{formatCurrency(preview.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="目標を削除しますか"
        message="按分で作られた月次目標も同時に削除されます。手で修正した月の目標は残ります。"
        confirmLabel="削除する"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </section>
  );
}

function PlanTaxConversion({ nodeId, months, currentAmount, onConverted }: {
  nodeId: string; months: string[]; currentAmount: string; onConverted: (taxExcluded: number) => void;
}) {
  const [taxIncluded, setTaxIncluded] = useState('');
  const [result, setResult] = useState<{ from: number; to: number; rate: number; monthsKey: string } | null>(null);
  const [error, setError] = useState<ConversionFailure | null>(null);
  const monthsKey = months.join(',');
  // 換算後に税抜欄・対象月を変えたら、古い「税込 → 税抜」表示は出さない
  const showResult = result !== null && result.monthsKey === monthsKey && String(result.to) === currentAmount;
  const mutation = useMutation({
    mutationFn: (value: number) => apiFetch<ConversionResponse>('/kpi-targets/tax-excluded-conversions', {
      method: 'POST', body: JSON.stringify({ node_id: nodeId, items: [{ months, value }] }),
    }),
    onSuccess: (r, value) => {
      const item = r.items[0];
      if (!item || item.error || item.converted_value === null || item.standard_rate === null) {
        setResult(null);
        setError({ message: item?.error ?? '税抜に換算できませんでした', code: item?.error_code ?? null });
        return;
      }
      setError(null);
      setResult({ from: value, to: item.converted_value, rate: item.standard_rate, monthsKey });
      onConverted(item.converted_value);
    },
    onError: (e: Error) => { setResult(null); setError({ message: e.message, code: null }); },
  });

  const parsed = parseTaxIncluded(taxIncluded);
  const invalid = taxIncluded.trim() !== '' && parsed === null;

  return (
    <details className="rounded border border-surface-border bg-white px-3 py-2">
      <summary className="min-h-[44px] flex items-center cursor-pointer text-sm text-text-body">税込額から計算する</summary>
      <div className="space-y-2 pb-1">
        <p className="text-xs text-text-sub">{TAX_NOTE}</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-full sm:w-60">
            <label className="block text-xs text-text-sub mb-1" htmlFor="kt-amount-incl">税込額（円）</label>
            <input
              id="kt-amount-incl" type="number" inputMode="numeric" className={numberInputCls}
              value={taxIncluded} aria-invalid={invalid}
              onChange={(e) => { setTaxIncluded(e.target.value); setResult(null); setError(null); }}
            />
          </div>
          <Button variant="secondary" loading={mutation.isPending} disabled={parsed === null || mutation.isPending}
            onClick={() => parsed !== null && mutation.mutate(parsed)}>
            税抜に換算
          </Button>
        </div>
        {invalid && <p className="text-xs text-danger-text">0以上の整数で入力してください</p>}
        {error && <TaxConversionError failure={error} />}
        {showResult && result && (
          <p className="text-sm text-text-body" aria-live="polite">
            税込 <span className="tabular-nums">{formatCurrency(result.from)}</span> → 税抜 <span className="tabular-nums">{formatCurrency(result.to)}</span>
            （標準税率{formatRatePercent(result.rate)}）
          </p>
        )}
      </div>
    </details>
  );
}

function WeightsEditor({ months, weights, onChange, disabled }: {
  months: string[]; weights: Record<string, number>; onChange: (w: Record<string, number>) => void; disabled: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-text-sub mb-2">月別の比率 (相対値。合計が100である必要はありません)</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {months.map((m) => (
          <div key={m}>
            <label className="block text-xs text-text-sub mb-1" htmlFor={`w-${m}`}>{monthLabel(m)}</label>
            <input
              id={`w-${m}`} type="number" inputMode="decimal" className={numberInputCls}
              value={weights[m] ?? ''} disabled={disabled}
              onChange={(e) => onChange({ ...weights, [m]: Number(e.target.value || 0) })}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthlyTable({ months, targetByMonth, nodeId, canManage, canConvertTax, onSaved }: {
  months: string[]; targetByMonth: Map<string, TargetRow>; nodeId: string; canManage: boolean;
  canConvertTax: boolean; onSaved: () => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [taxMode, setTaxMode] = useState(false);
  const [taxIncluded, setTaxIncluded] = useState<Record<string, string>>({});
  const [taxErrors, setTaxErrors] = useState<Record<string, ConversionFailure>>({});
  const taxTargets = months
    .map((m) => ({ month: m, value: parseTaxIncluded(taxIncluded[m] ?? '') }))
    .filter((t): t is { month: string; value: number } => t.value !== null);
  const hasInvalidTaxInput = months.some((m) => (taxIncluded[m] ?? '').trim() !== '' && parseTaxIncluded(taxIncluded[m]!) === null);
  const convertMutation = useMutation({
    mutationFn: (targets: { month: string; value: number }[]) => apiFetch<ConversionResponse>('/kpi-targets/tax-excluded-conversions', {
      method: 'POST',
      body: JSON.stringify({ node_id: nodeId, items: targets.map((t) => ({ months: [t.month], value: t.value })) }),
    }).then((r) => ({ r, targets })),
    onSuccess: ({ r, targets }) => {
      const nextDrafts = { ...drafts };
      const nextErrors: Record<string, ConversionFailure> = {};
      targets.forEach((t, i) => {
        const item = r.items[i];
        if (item && item.converted_value !== null && !item.error) nextDrafts[t.month] = String(item.converted_value);
        else nextErrors[t.month] = { message: item?.error ?? '税抜に換算できませんでした', code: item?.error_code ?? null };
      });
      setDrafts(nextDrafts);
      setTaxErrors(nextErrors);
      const failed = Object.keys(nextErrors).length;
      if (failed === 0) toast.success(`${targets.length}か月分を税抜に換算しました（未保存）`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/kpi-targets/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('目標を削除しました'); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const saveMutation = useMutation({
    mutationFn: () => apiFetch('/kpi-targets/months', {
      method: 'PUT',
      body: JSON.stringify({
        node_id: nodeId,
        entries: Object.entries(drafts)
          .filter(([, v]) => v !== '')
          .map(([month, v]) => ({ target_month: month, target_value: Number(v) })),
      }),
    }),
    onSuccess: () => { toast.success('月次目標を保存しました'); setDrafts({}); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const dirtyCount = Object.values(drafts).filter((v) => v !== '').length;

  return (
    <section className="rounded border border-surface-border bg-surface-card p-4 space-y-3">
      <h2 className="text-sm font-semibold text-text-main">月別の目標</h2>
      <p className="text-xs text-text-sub">
        値を書き換えると「手修正」になり、以後この月は按分をやり直しても上書きされません。
      </p>
      {canConvertTax && canManage && (
        <div className="space-y-2">
          <Button variant="secondary" size="sm" onClick={() => setTaxMode(!taxMode)}>
            {taxMode ? '税込入力を閉じる' : '税込で入力'}
          </Button>
          {taxMode && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-text-sub">税込額を入力して「税抜に換算」を押すと、目標額（税抜）欄を置き換えます。{TAX_NOTE}</p>
              <Button variant="secondary" size="sm" loading={convertMutation.isPending}
                disabled={taxTargets.length === 0 || hasInvalidTaxInput || convertMutation.isPending}
                onClick={() => convertMutation.mutate(taxTargets)}>
                税抜に換算
              </Button>
              {hasInvalidTaxInput && <p className="w-full text-xs text-danger-text">税込額は0以上の整数で入力してください</p>}
            </div>
          )}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="relative min-w-full text-sm">
          <caption className="sr-only">月別の売上目標</caption>
          <thead>
            <tr className="text-left text-text-sub border-b border-surface-border">
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap">月</th>
              {taxMode && <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap">税込額（円）</th>}
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap">目標額（税抜、円）</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap">由来</th>
              <th scope="col" className="relative py-2 font-medium whitespace-nowrap"><span className="sr-only">操作</span></th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const row = targetByMonth.get(m);
              const draft = drafts[m];
              return (
                <tr key={m} className="border-b border-surface-line">
                  <td className="py-2 pr-4 whitespace-nowrap align-top">{monthLabel(m)}</td>
                  {taxMode && (
                    <td className="py-2 pr-4 align-top">
                      <input
                        aria-label={`${monthLabel(m)}の税込額`}
                        type="number" inputMode="numeric" className={`${numberInputCls} w-40`}
                        value={taxIncluded[m] ?? ''}
                        aria-invalid={(taxIncluded[m] ?? '').trim() !== '' && parseTaxIncluded(taxIncluded[m]!) === null}
                        onChange={(e) => setTaxIncluded({ ...taxIncluded, [m]: e.target.value })}
                      />
                      {taxErrors[m] && <TaxConversionError failure={taxErrors[m]!} />}
                    </td>
                  )}
                  <td className="py-2 pr-4 align-top">
                    <input
                      aria-label={`${monthLabel(m)}の目標額（税抜）`}
                      type="number" inputMode="numeric" className={`${numberInputCls} w-40`}
                      disabled={!canManage}
                      value={draft ?? (row ? String(row.target_value) : '')}
                      onChange={(e) => setDrafts({ ...drafts, [m]: e.target.value })}
                    />
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap align-top">
                    {row
                      ? <SourceBadge source={draft !== undefined && draft !== String(row.target_value) ? 'manual' : row.source} />
                      : <span className="text-text-sub">−</span>}
                  </td>
                  <td className="py-2 whitespace-nowrap align-top">
                    {/* ⚠️ 入力欄を空にしても保存対象から落ちるだけで何も起きない (「保存できていない」
                        と誤認される)。目標を取り消す導線を明示的に置く。 */}
                    {row && (
                      <button
                        type="button"
                        aria-label={`${monthLabel(m)}の目標を削除`}
                        disabled={!canManage || deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(row.kpi_target_id)}
                        className="inline-flex items-center justify-center w-11 h-11 rounded text-text-sub hover:bg-surface-sunken disabled:opacity-40"
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* ⚠️ ui-guidelines §2.2「赤は1画面1箇所」: primary (赤) は按分の保存に1つだけ置く */}
      <Button variant="secondary" leftIcon={<Save size={16} />} onClick={() => saveMutation.mutate()}
        loading={saveMutation.isPending} disabled={!canManage || dirtyCount === 0}>
        月別の目標を保存 ({dirtyCount}件)
      </Button>
    </section>
  );
}

function SourceBadge({ source }: { source: 'manual' | 'plan_allocated' }) {
  return source === 'manual'
    ? <span className="inline-flex items-center rounded px-2 py-0.5 text-xs bg-alert-bg text-alert-text border border-alert-border">手修正</span>
    : <span className="inline-flex items-center rounded px-2 py-0.5 text-xs bg-surface-sunken text-text-sub border border-surface-border">按分</span>;
}

function FooterSummary({ planValue, monthlyTotal }: { planValue: number | null; monthlyTotal: number }) {
  if (planValue == null) {
    return <p className="text-sm text-text-body">月次合計: <span className="tabular-nums">{formatCurrency(monthlyTotal)}</span></p>;
  }
  const gap = monthlyTotal - planValue;
  return (
    <p className="text-sm text-text-body">
      期間目標: <span className="tabular-nums">{formatCurrency(planValue)}</span>
      {' / '}月次合計: <span className="tabular-nums">{formatCurrency(monthlyTotal)}</span>
      {gap !== 0 && <> (差 <span className="tabular-nums">{gap > 0 ? '+' : ''}{formatCurrency(gap)}</span>)</>}
    </p>
  );
}
